import { useState } from 'react'
import { useDocumentTitle } from '@/shared/hooks/use-document-title'
import { Card } from '@/shared/components/ui/card'
import { Skeleton } from '@/shared/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { AlertCircle, ListChecks } from 'lucide-react'
import { ColaTable } from '../components'
import { useAprobarItem, useCola, useRechazarItem } from '@/modules/bd-unificada/hooks/useCola'
import type { EstadoCola } from '@/modules/bd-unificada/types/cola'
import { ESTADOS_COLA } from '@/modules/bd-unificada/types/cola'

export function ColaPage() {
  useDocumentTitle('BD Unificada — Cola de verificación')

  const [estado, setEstado] = useState<EstadoCola>('pendiente')
  const [pagina, setPagina] = useState(1)

  const { data, isLoading } = useCola({ estado, pagina, por_pagina: 25 })
  const aprobar = useAprobarItem()
  const rechazar = useRechazarItem()

  const procesandoId = aprobar.isPending
    ? (aprobar.variables as string)
    : rechazar.isPending
      ? (rechazar.variables as string)
      : null

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <ListChecks className="h-6 w-6 text-blue-500" />
          BD Unificada — Cola de verificación
        </h1>
        <Select value={estado} onValueChange={(v) => { setEstado(v as EstadoCola); setPagina(1) }}>
          <SelectTrigger className="w-[200px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ESTADOS_COLA.map((e) => (
              <SelectItem key={e.value} value={e.value}>
                {e.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card className="border border-gray-300 dark:border-gray-700 p-6">
        {isLoading ? (
          <div className="space-y-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : data && data.items.length > 0 ? (
          <ColaTable
            items={data.items}
            soloLectura={estado !== 'pendiente'}
            onAprobar={(id) => aprobar.mutate(id)}
            onRechazar={(id) => rechazar.mutate(id)}
            procesandoId={procesandoId}
          />
        ) : (
          <div className="p-12 text-center">
            <AlertCircle className="mx-auto h-12 w-12 text-muted-foreground/50" />
            <h3 className="mt-4 text-lg font-semibold">Sin items en esta vista</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              No hay registros con estado "{estado}".
            </p>
          </div>
        )}
      </Card>
    </div>
  )
}
